import { LitElement, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { BroadcasterTelemetry, type BroadcasterTelemetrySnapshot } from "./broadcaster-telemetry";
import { CanvasCompositor, type CompositorOrientation } from "./canvas-compositor";
import { styles } from "./stream-recorder.css";

declare global {
  // Chrome 109+ CaptureController API — not yet in all TS type libs
  class CaptureController {
    setFocusBehavior(behavior: "focus-captured-surface" | "no-focus-change"): void;
  }
  interface DisplayMediaStreamOptions {
    controller?: CaptureController;
  }
}

type RecorderState = "idle" | "starting" | "streaming" | "error";
type TelemetryMode = "local" | "remote" | "disabled";

/**
 * Stream Recorder component - WebRTC WHIP streaming from webcam/microphone.
 * Uses WHIP protocol to publish media to a streaming server.
 * Always runs through a canvas compositor — camera-only initially,
 * screen-share + camera PiP when screen sharing is active.
 */
@customElement("stream-recorder")
export class StreamRecorder extends LitElement {
  static override styles = styles;

  @property() streamId?: string;

  @property({ type: Boolean, reflect: true, attribute: "show-preview" }) showPreview = true;

  @property({ type: Boolean, attribute: "enable-audio" }) enableAudio = true;

  @property({ type: Boolean, attribute: "hide-controls" }) hideControls = false;

  @property({ attribute: "telemetry-mode" }) telemetryMode: TelemetryMode = "disabled";

  maxVideoBitrate = 4500000; // 4500 kbps — overshoot for better FFmpeg re-encode quality

  maxAudioBitrate = 128000; // 128 kbps

  maxFramerate = 24;

  @state() private isPaused = false;

  @property({ type: Boolean, reflect: true, attribute: "screen-sharing" })
  private isScreenSharing = false;

  @property({ type: Boolean, reflect: true, attribute: "rear-camera" })
  private isRearCamera = false;

  private get WHIP_BASE_URL(): string {
    const isLocalhost = (location.origin ?? "localhost").includes("localhost");
    const isProduction = location.origin.includes("worldofwow.com");
    const liveDomain = isLocalhost
      ? "live.worldofwow.dev"
      : isProduction
        ? "live.worldofwow.com"
        : "live.worldofwow.dev";
    return `https://${liveDomain}:8889/streams/`;
  }

  private get whipEndpoint(): string {
    return `${this.WHIP_BASE_URL}${this.streamId}/whip`;
  }

  /**
   * Raw fetch without Application Insights instrumentation.
   * AI adds request-context headers that cause CORS issues with the streaming server.
   */
  private async rawFetch(
    url: string,
    options: { method: string; headers: Record<string, string>; body?: string; signal?: AbortSignal },
  ): Promise<{
    ok: boolean;
    status: number;
    text: () => Promise<string>;
    headers: { get: (name: string) => string | null };
  }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method, url, true);

      for (const [key, value] of Object.entries(options.headers)) {
        xhr.setRequestHeader(key, value);
      }

      xhr.onload = () => {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          text: () => Promise.resolve(xhr.responseText),
          headers: {
            get: (name: string) => xhr.getResponseHeader(name),
          },
        });
      };

      xhr.onerror = () => reject(new Error("Network error"));
      xhr.onabort = () => reject(new DOMException("Request aborted", "AbortError"));

      if (options.signal) {
        options.signal.addEventListener("abort", () => xhr.abort(), { once: true });
      }

      xhr.send(options.body);
    });
  }

  @query("video")
  private videoElement?: HTMLVideoElement;

  @state() private recorderState: RecorderState = "idle";

  @state() private errorMessage = "";

  private pc: RTCPeerConnection | null = null;
  private mediaStream: MediaStream | null = null;
  private whipResourceURL: string | null = null;

  private screenShareStream: MediaStream | null = null;
  private compositor: CanvasCompositor | null = null;
  private telemetry: BroadcasterTelemetry | null = null;

  private _disconnectController: AbortController | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this._disconnectController = new AbortController();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectController?.abort(new Error("Component disconnected"));
    this._disconnectController = null;
    void this.stopStreaming();
  }

  async startStreaming(): Promise<void> {
    if (this.recorderState === "streaming" || this.recorderState === "starting") {
      return;
    }

    if (!this.streamId) {
      this.handleError("Stream ID is required");
      return;
    }

    this.recorderState = "starting";
    this.errorMessage = "";

    const abortSignal = this._disconnectController?.signal;

    try {
      // Detect device orientation at stream start, lock for duration
      const isPortrait = screen.orientation?.type?.includes("portrait") ?? window.innerHeight >= window.innerWidth;

      const videoConstraints: MediaTrackConstraints = {
        facingMode: { ideal: "user" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 24, max: this.maxFramerate },
      };

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: this.enableAudio,
        });
      } catch (e) {
        if (e instanceof OverconstrainedError) {
          this.mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "user" } },
            audio: this.enableAudio,
          });
        } else {
          throw e;
        }
      }

      // Component may have disconnected while waiting for camera permission
      if (abortSignal?.aborted) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.mediaStream = null;
        this.recorderState = "idle";
        return;
      }

      this.compositor = new CanvasCompositor(isPortrait ? "portrait" : "landscape");
      const cameraStream = new MediaStream(this.mediaStream.getVideoTracks());
      this.compositor.startCameraOnly(cameraStream);
      await this.compositor.waitUntilReady();

      if (abortSignal?.aborted) {
        await this.stopStreaming();
        return;
      }

      const compositedTrack = this.compositor.getVideoTrack();
      if (!compositedTrack) {
        throw new Error("Failed to get composited video track");
      }

      // Show composited preview (CSS scaleX(-1) handles self-view mirror)
      if (this.videoElement && this.showPreview) {
        const previewStream = new MediaStream([compositedTrack]);
        const audioTrack = this.mediaStream.getAudioTracks()[0];
        if (audioTrack) previewStream.addTrack(audioTrack);
        this.videoElement.srcObject = previewStream;
      }

      // Create peer connection
      this.pc = new RTCPeerConnection({
        iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
      });

      // Add composited video + original audio to peer connection
      this.pc.addTrack(compositedTrack);
      const audioTrack = this.mediaStream.getAudioTracks()[0];
      if (audioTrack) {
        this.pc.addTrack(audioTrack, this.mediaStream);
      }

      // Set codec preferences for H.264 (HLS compatibility)
      await this.setCodecPreferences();

      // Apply bitrate constraints
      await this.applyBitrateConstraints();

      // Handle ICE candidates
      this.pc.onicecandidate = async (event) => {
        if (!event.candidate || !this.whipResourceURL) return;

        try {
          await this.rawFetch(this.whipResourceURL, {
            method: "PATCH",
            headers: { "Content-Type": "application/trickle-ice-sdpfrag" },
            body: `a=${event.candidate.candidate}`,
          });
        } catch (error) {
          void error;
        }
      };

      // Monitor connection state
      this.pc.onconnectionstatechange = () => {
        if (!this.pc) return;

        switch (this.pc.connectionState) {
          case "connected":
            this.recorderState = "streaming";
            this.startTelemetry();
            this.dispatchEvent(new CustomEvent("streaming-started", { bubbles: true, composed: true }));
            break;
          case "disconnected":
            break;
          case "failed":
            this.handleError("Connection failed. Please retry.");
            break;
          case "closed":
            if (this.recorderState === "streaming") {
              this.recorderState = "idle";
              this.dispatchEvent(new CustomEvent("streaming-stopped", { bubbles: true, composed: true }));
            }
            break;
        }
      };

      // Create and send offer
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      // POST to WHIP endpoint (use rawFetch to bypass Application Insights headers)
      const response = await this.rawFetch(this.whipEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp,
        signal: abortSignal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`WHIP POST failed (${response.status}): ${errorText.slice(0, 100)}`);
      }

      // MediaMTX returns a relative Location header (e.g., /streams/{id}/whip/{secret})
      // We must resolve it against the WHIP endpoint base URL, not the page origin
      const locationHeader = response.headers.get("Location");
      if (locationHeader) {
        this.whipResourceURL = new URL(locationHeader, this.WHIP_BASE_URL).href;
      }
      const answerSdp = await response.text();

      await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to start streaming";
      this.handleError(message);
    }
  }

  async stopStreaming(): Promise<void> {
    // Stop screen sharing first if active
    if (this.isScreenSharing) {
      await this.stopScreenShare();
    }

    // Stop compositor
    if (this.compositor) {
      this.compositor.stop();
      this.compositor = null;
    }

    // Stop all media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Stop telemetry before closing peer connection
    this.stopTelemetry();

    // Close peer connection
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    // Clear video preview
    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    this.whipResourceURL = null;

    if (this.recorderState === "streaming") {
      this.dispatchEvent(new CustomEvent("streaming-stopped", { bubbles: true, composed: true }));
    }

    this.recorderState = "idle";
    this.isPaused = false;
    this.isScreenSharing = false;
    this.errorMessage = "";
  }

  pauseStreaming(): void {
    if (this.recorderState !== "streaming" || this.isPaused) return;

    // Disable composited video track (viewers see black)
    const videoTrack = this.compositor?.getVideoTrack();
    if (videoTrack) videoTrack.enabled = false;

    // Disable audio
    this.mediaStream?.getAudioTracks().forEach((t) => {
      t.enabled = false;
    });

    // Pause render loop (save CPU)
    this.compositor?.pauseRendering();

    this.isPaused = true;
    this.dispatchEvent(new CustomEvent("streaming-paused", { bubbles: true, composed: true }));
  }

  resumeStreaming(): void {
    if (this.recorderState !== "streaming" || !this.isPaused) return;

    // Re-enable composited video track
    const videoTrack = this.compositor?.getVideoTrack();
    if (videoTrack) videoTrack.enabled = true;

    // Re-enable audio
    this.mediaStream?.getAudioTracks().forEach((t) => {
      t.enabled = true;
    });

    // Resume render loop
    this.compositor?.resumeRendering();

    this.isPaused = false;
    this.dispatchEvent(new CustomEvent("streaming-resumed", { bubbles: true, composed: true }));
  }

  toggleCamera(): void {
    if (this.recorderState !== "streaming" || !this.mediaStream) return;

    const videoTracks = this.mediaStream.getVideoTracks();
    const enabled = videoTracks.length > 0 ? !videoTracks[0]!.enabled : false;
    videoTracks.forEach((track) => {
      track.enabled = enabled;
    });

    this.dispatchEvent(
      new CustomEvent(enabled ? "camera-enabled" : "camera-disabled", { bubbles: true, composed: true }),
    );
  }

  toggleMicrophone(): void {
    if (this.recorderState !== "streaming" || !this.mediaStream) return;

    const audioTracks = this.mediaStream.getAudioTracks();
    const enabled = audioTracks.length > 0 ? !audioTracks[0]!.enabled : false;
    audioTracks.forEach((track) => {
      track.enabled = enabled;
    });

    this.dispatchEvent(
      new CustomEvent(enabled ? "microphone-enabled" : "microphone-disabled", { bubbles: true, composed: true }),
    );
  }

  private currentFacingMode: "user" | "environment" = "user";

  async switchCamera(): Promise<void> {
    if (this.recorderState !== "streaming" || !this.mediaStream || !this.compositor) return;

    const nextFacing = this.currentFacingMode === "user" ? "environment" : "user";

    try {
      const switchVideoConstraints: MediaTrackConstraints = {
        facingMode: { exact: nextFacing },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 24, max: this.maxFramerate },
      };

      let newStream: MediaStream;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: switchVideoConstraints,
          audio: false,
        });
      } catch (e) {
        if (e instanceof OverconstrainedError) {
          newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { exact: nextFacing } },
            audio: false,
          });
        } else {
          throw e;
        }
      }

      // Stop old video track
      this.mediaStream.getVideoTracks().forEach((t) => t.stop());

      // Replace video track in mediaStream
      const newVideoTrack = newStream.getVideoTracks()[0]!;
      this.mediaStream.getVideoTracks().forEach((t) => this.mediaStream!.removeTrack(t));
      this.mediaStream.addTrack(newVideoTrack);

      // Update compositor with new camera stream
      const cameraStream = new MediaStream([newVideoTrack]);
      this.compositor.updateCameraStream(cameraStream);

      this.currentFacingMode = nextFacing;
      this.isRearCamera = nextFacing === "environment";
      this.dispatchEvent(
        new CustomEvent("camera-switched", {
          detail: { facingMode: nextFacing },
          bubbles: true,
          composed: true,
        }),
      );
    } catch {
      // Device may not have a rear camera
    }
  }

  async switchOrientation(): Promise<void> {
    if (this.recorderState !== "streaming" || !this.mediaStream || !this.compositor) return;

    const current = this.compositor.getOrientation();
    const next: CompositorOrientation = current === "landscape" ? "portrait" : "landscape";

    this.compositor.setOrientation(next);
    this.dispatchEvent(
      new CustomEvent("orientation-changed", {
        detail: { orientation: next },
        bubbles: true,
        composed: true,
      }),
    );
  }

  getOrientation(): CompositorOrientation {
    return this.compositor?.getOrientation() ?? "landscape";
  }

  isCameraEnabled(): boolean {
    return this.mediaStream?.getVideoTracks().some((t) => t.enabled) ?? false;
  }

  isMicrophoneEnabled(): boolean {
    return this.mediaStream?.getAudioTracks().some((t) => t.enabled) ?? false;
  }

  getIsPaused(): boolean {
    return this.isPaused;
  }

  getRecorderState(): RecorderState {
    return this.recorderState;
  }

  getIsScreenSharing(): boolean {
    return this.isScreenSharing;
  }

  /**
   * Check if screen sharing is supported on this device.
   * Returns false on iOS devices which don't support getDisplayMedia.
   */
  static isScreenShareSupported(): boolean {
    // iOS does not support getDisplayMedia
    const isIOS =
      /iP(ad|hone|od)/.test(navigator.platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);

    if (isIOS) return false;

    return typeof navigator.mediaDevices?.getDisplayMedia === "function";
  }

  /**
   * Start screen sharing. The compositor switches to screen-share mode:
   * screen as fullscreen, camera as PiP overlay. No track replacement needed.
   */
  async startScreenShare(): Promise<void> {
    if (this.recorderState !== "streaming") {
      return;
    }

    if (this.isScreenSharing) {
      return;
    }

    if (!StreamRecorder.isScreenShareSupported()) {
      this.dispatchEvent(
        new CustomEvent("screen-share-error", {
          detail: { message: "Screen sharing is not supported on this device" },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }

    try {
      // Prevent browser from switching focus to the shared tab (Chrome 109+)
      const controller = typeof CaptureController !== "undefined" ? new CaptureController() : undefined;

      this.screenShareStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 24 },
        },
        audio: false,
        ...(controller ? { controller } : {}),
      });

      controller?.setFocusBehavior("no-focus-change");
      // Set contentHint on raw capture track to hint browser's capture pipeline
      const screenTrack = this.screenShareStream.getVideoTracks()[0];
      if (screenTrack) {
        screenTrack.contentHint = "detail";
        screenTrack.onended = () => {
          void this.stopScreenShare();
        };
      }

      // Add screen stream to compositor — switches to screen-share mode instantly
      this.compositor!.addScreenStream(this.screenShareStream);
      this.isScreenSharing = true;

      const compositedTrack = this.compositor!.getVideoTrack();
      if (compositedTrack) {
        compositedTrack.contentHint = "detail";
      }

      // Prefer maintaining resolution for text sharpness during screen share
      const senders = this.pc?.getSenders();
      if (senders) {
        for (const sender of senders) {
          if (sender.track?.kind === "video") {
            const params = sender.getParameters();
            params.degradationPreference = "maintain-resolution";
            await sender.setParameters(params);
          }
        }
      }

      this.dispatchEvent(new CustomEvent("screen-share-started", { bubbles: true, composed: true }));
    } catch (error) {
      // User cancelled the screen share picker
      if (error instanceof Error && error.name === "NotAllowedError") {
        // User cancelled screen share picker
        return;
      }

      // Clean up on failure
      if (this.screenShareStream) {
        this.screenShareStream.getTracks().forEach((track) => track.stop());
        this.screenShareStream = null;
      }

      const message = error instanceof Error ? error.message : "Failed to start screen share";
      this.dispatchEvent(
        new CustomEvent("screen-share-error", {
          detail: { message },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  /**
   * Stop screen sharing — compositor switches back to camera-only mode instantly.
   */
  async stopScreenShare(): Promise<void> {
    if (!this.isScreenSharing) {
      return;
    }

    this.compositor?.removeScreenStream();

    if (this.screenShareStream) {
      this.screenShareStream.getTracks().forEach((track) => track.stop());
      this.screenShareStream = null;
    }

    this.isScreenSharing = false;

    const compositedTrack = this.compositor?.getVideoTrack();
    if (compositedTrack) {
      compositedTrack.contentHint = "";
    }

    // Revert degradation preference to default
    const senders = this.pc?.getSenders();
    if (senders) {
      for (const sender of senders) {
        if (sender.track?.kind === "video") {
          const params = sender.getParameters();
          params.degradationPreference = "balanced";
          await sender.setParameters(params);
        }
      }
    }

    this.dispatchEvent(new CustomEvent("screen-share-stopped", { bubbles: true, composed: true }));
  }

  private async setCodecPreferences(): Promise<void> {
    if (!this.pc) return;

    const codecs = RTCRtpSender.getCapabilities("video")?.codecs ?? [];
    const transceivers = this.pc.getTransceivers();
    for (const transceiver of transceivers) {
      if (transceiver.sender.track?.kind === "video") {
        // Prefer higher H.264 profiles for better compression on WHIP input.
        // Server re-encodes to main for HLS output, so input profile is free.
        const h264High = codecs.filter(
          (c) => c.mimeType === "video/H264" && c.sdpFmtpLine?.includes("profile-level-id=64"),
        );
        const h264Main = codecs.filter(
          (c) => c.mimeType === "video/H264" && c.sdpFmtpLine?.includes("profile-level-id=4d"),
        );
        const h264Baseline = codecs.filter(
          (c) => c.mimeType === "video/H264" && c.sdpFmtpLine?.includes("profile-level-id=42"),
        );
        const h264Other = codecs.filter(
          (c) =>
            c.mimeType === "video/H264" &&
            !c.sdpFmtpLine?.includes("profile-level-id=64") &&
            !c.sdpFmtpLine?.includes("profile-level-id=4d") &&
            !c.sdpFmtpLine?.includes("profile-level-id=42"),
        );
        const vp8Codecs = codecs.filter((c) => c.mimeType === "video/VP8");

        const preferredCodecs = [...h264High, ...h264Main, ...h264Baseline, ...h264Other, ...vp8Codecs];

        if (preferredCodecs.length > 0) {
          try {
            transceiver.setCodecPreferences(preferredCodecs);
          } catch {
            // setCodecPreferences not supported or failed
          }
        }
      }
    }
  }

  private async applyBitrateConstraints(): Promise<void> {
    if (!this.pc) return;

    const senders = this.pc.getSenders();
    for (const sender of senders) {
      if (sender.track?.kind === "video") {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        const encoding = params.encodings[0]!;
        encoding.maxBitrate = this.maxVideoBitrate;
        encoding.maxFramerate = this.maxFramerate;
        await sender.setParameters(params);
      }
      if (sender.track?.kind === "audio") {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        const encoding = params.encodings[0]!;
        encoding.maxBitrate = this.maxAudioBitrate;
        await sender.setParameters(params);
      }
    }
  }

  private extractSessionIdFromSdp(localOffer: string, remoteAnswer: string): string {
    const localStart = localOffer.indexOf("a=ice-ufrag:") + "a=ice-ufrag:".length;
    let sessionid = localOffer.substring(localStart);
    sessionid = sessionid.substring(0, sessionid.indexOf("\n") - 1) + ":";

    const remoteStart = remoteAnswer.indexOf("a=ice-ufrag:") + "a=ice-ufrag:".length;
    sessionid += remoteAnswer.substring(remoteStart);
    sessionid = sessionid.substring(0, sessionid.indexOf("\n"));
    return sessionid.trim();
  }

  private startTelemetry(): void {
    if (this.telemetryMode === "disabled" || !this.pc || !this.streamId) return;

    // Extract session ID from SDP
    const localDesc = this.pc.localDescription;
    const remoteDesc = this.pc.remoteDescription;
    if (!localDesc?.sdp || !remoteDesc?.sdp) return;

    const sessionId = this.extractSessionIdFromSdp(localDesc.sdp, remoteDesc.sdp);

    this.telemetry = new BroadcasterTelemetry(
      this.streamId,
      sessionId,
      this.pc,
      this.telemetryMode,
      (snapshot: BroadcasterTelemetrySnapshot) => {
        this.dispatchEvent(
          new CustomEvent("telemetry-snapshot", {
            bubbles: true,
            composed: true,
            detail: snapshot,
          }),
        );
      },
    );
    this.telemetry.start();
  }

  private stopTelemetry(): void {
    if (this.telemetry) {
      this.telemetry.stop();
      this.telemetry = null;
    }
  }

  private handleError(message: string): void {
    this.recorderState = "error";
    this.errorMessage = message;
    this.dispatchEvent(
      new CustomEvent("streaming-error", {
        detail: { message },
        bubbles: true,
        composed: true,
      }),
    );
    void this.stopStreaming();
  }

  private handleStartClick = (): void => {
    void this.startStreaming();
  };

  private handleStopClick = (): void => {
    void this.stopStreaming();
  };

  private getStatusText(): string {
    switch (this.recorderState) {
      case "idle":
        return "Ready";
      case "starting":
        return "Starting...";
      case "streaming":
        return "Live";
      case "error":
        return "Error";
    }
  }

  override render() {
    const isStreaming = this.recorderState === "streaming";
    const isStarting = this.recorderState === "starting";
    const isError = this.recorderState === "error";

    const previewClasses = {
      "preview-wrapper": true,
      hidden: !this.showPreview,
      paused: this.isPaused,
    };

    const statusDotClasses = {
      "status-dot": true,
      streaming: isStreaming && !this.isPaused,
      starting: isStarting,
      error: isError,
      paused: this.isPaused,
    };

    return html`
      <div class="recorder-container">
        <div class=${classMap(previewClasses)}>
          <video autoplay muted playsinline></video>
          ${this.isPaused ? html`<div class="paused-overlay">PAUSED</div>` : null}
        </div>

        ${!this.hideControls
          ? html`
              <div class="controls">
                ${isStreaming
                  ? html` <button class="stream-button stop" @click=${this.handleStopClick}>Stop Streaming</button> `
                  : html`
                      <button class="stream-button start" ?disabled=${isStarting} @click=${this.handleStartClick}>
                        ${isStarting ? "Starting..." : "Start Streaming"}
                      </button>
                    `}

                <div class="status-indicator">
                  <div class=${classMap(statusDotClasses)}></div>
                  <span>${this.getStatusText()}</span>
                </div>
              </div>
            `
          : null}
        ${this.errorMessage ? html` <div class="error-message">${this.errorMessage}</div> ` : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stream-recorder": StreamRecorder;
  }
}
